import React, { useEffect, useState, useRef } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { api, COLLECTIONS } from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clientes from "@/pages/Clientes";
import Articulos from "@/pages/Articulos";
import Servicios from "@/pages/Servicios";
import Personal from "@/pages/Personal";
import Proyectos from "@/pages/Proyectos";
import Ventas from "@/pages/Ventas";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import PasskeysModal from "@/components/PasskeysModal";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_desktop-converter-9/artifacts/lfkzv9nu_IMG_0020.PNG";

const ICONS = {
  grid: "M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v6H4zM14 15h6v6h-6z",
  users: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-2a4 4 0 100-8 4 4 0 000 8zm6 0a3 3 0 100-6 3 3 0 000 6z",
  box: "M21 16V8l-9-4-9 4v8l9 4 9-4zM3 8l9 4 9-4",
  spark: "M12 2l2.39 5.48L20 9l-4.32 3.76L17 19l-5-2.9L7 19l1.32-6.24L4 9l5.61-1.52L12 2z",
  id: "M3 7h18v10H3zM7 11a2 2 0 100-4 2 2 0 000 4zm4 4h6M11 11h6",
  folder: "M3 7a2 2 0 012-2h3l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z",
  cart: "M3 3h2l2 12h13l2-9H6",
  chart: "M3 3v18h18M7 15l4-4 4 4 6-6",
  shield: "M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z",
};

const SvgIcon = ({ d }) => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={d} />
  </svg>
);

function Shell() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("dashboard");
  const [state, setState] = useState({
    clientes: [], articulos: [], servicios: [], personal: [], proyectos: [], ventas: [],
  });
  const [clock, setClock] = useState("");
  const [showChangePw, setShowChangePw] = useState(false);
  const [showPasskeys, setShowPasskeys] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        await api.seed().catch(() => {});
        const all = {};
        for (const col of COLLECTIONS) {
          const res = await api.list(col, { skip: 0, limit: 10000 });
          all[col] = res.items || [];
        }
        setState(all);
      } catch (e) {
        console.error(e);
        toast.error("Error cargando datos del servidor");
      }
    })();
  }, []);

  useEffect(() => {
    const tick = () =>
      setClock(new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, []);

  const upsert = async (col, item) => {
    if (item.id) {
      await api.update(col, item.id, item);
      setState((s) => ({ ...s, [col]: s[col].map((r) => (r.id === item.id ? item : r)) }));
    } else {
      const created = await api.create(col, item);
      setState((s) => ({ ...s, [col]: [...s[col], created] }));
    }
  };

  const remove = async (col, id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    await api.remove(col, id);
    setState((s) => ({ ...s, [col]: s[col].filter((r) => r.id !== id) }));
    toast.success("Eliminado");
  };

  const isAdmin = user?.role === "admin";
  const nav = [
    { key: "dashboard", label: "Panel", icon: ICONS.grid, count: () => "" },
    { key: "clientes", label: "Clientes", icon: ICONS.users, count: (s) => s.clientes.length },
    { key: "articulos", label: "Artículos", icon: ICONS.box, count: (s) => s.articulos.length },
    { key: "servicios", label: "Servicios", icon: ICONS.spark, count: (s) => s.servicios.length },
    { key: "personal", label: "Personal", icon: ICONS.id, count: (s) => s.personal.length },
    { key: "proyectos", label: "Proyectos", icon: ICONS.folder, count: (s) => s.proyectos.length },
    { key: "ventas", label: "Ventas", icon: ICONS.cart, count: (s) => s.ventas.length },
    { key: "informes", label: "Informes", icon: ICONS.chart, count: () => "" },
    ...(isAdmin ? [{ key: "usuarios", label: "Usuarios", icon: ICONS.shield, count: () => "" }] : []),
  ];

  const subtitleMap = {
    dashboard: "Resumen y avisos del día",
    clientes: "Gestión de clientes",
    articulos: "Catálogo de productos",
    servicios: "Servicios ofertados",
    personal: "Equipo, PRL y reconocimientos",
    proyectos: "Obras y vinculación de recursos",
    ventas: "Facturación y pedidos",
    informes: "Analítica de facturación y clientes",
    usuarios: "Gestión de usuarios y roles",
  };

  const currentLabel = nav.find((n) => n.key === view)?.label;

  const doBackup = async () => {
    const backup = await api.backup();
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    a.download = `convitel-backup-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Backup descargado");
  };

  const restoreBackup = (ev) => {
    const f = ev.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const j = JSON.parse(reader.result);
        if (!window.confirm("Esto reemplazará todos los datos actuales. ¿Continuar?")) return;
        await api.restore(j);
        const all = {};
        for (const col of COLLECTIONS) {
          const res = await api.list(col, { skip: 0, limit: 10000 });
          all[col] = res.items || [];
        }
        setState(all);
        toast.success("Backup restaurado");
      } catch (e) {
        toast.error("Error al leer backup: " + e.message);
      }
    };
    reader.readAsText(f);
    ev.target.value = "";
  };

  const commonProps = { state, setState, upsert, remove, setView };

  return (
    <div className="min-h-screen flex">
      {/* Mobile overlay when sidebar open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      <aside className={`w-64 shrink-0 bg-[#0E1726] text-white flex flex-col fixed md:static inset-y-0 left-0 z-40 transform transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Convitel" className="w-11 h-11 rounded-lg bg-white p-1 object-contain"/>
            <div>
              <div className="font-extrabold text-base leading-tight">CONVITEL</div>
              <div className="text-[11px] text-white/60 font-medium tracking-wide">COMUNICACIONES · ERP</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((item) => (
            <div key={item.key}
                 className={`sidebar-item ${view === item.key ? "active" : ""}`}
                 onClick={() => { setView(item.key); setSidebarOpen(false); }}
                 data-testid={`nav-${item.key}`}>
              <SvgIcon d={item.icon} />
              <span>{item.label}</span>
              <span className="ml-auto text-xs opacity-60">{item.count(state)}</span>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-2">
          <button onClick={doBackup} className="w-full sidebar-item" data-testid="btn-backup">
            <SvgIcon d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
            <span>Backup</span>
          </button>
          <label className="w-full sidebar-item cursor-pointer">
            <SvgIcon d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M17 8l-5-5m0 0L7 8m5-5v12" />
            <span>Restaurar</span>
            <input ref={fileInputRef} type="file" accept=".json" className="hidden"
                   onChange={restoreBackup} data-testid="input-restore"/>
          </label>
          <div className="px-3 pt-3 border-t border-white/10 mt-2">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Sesión</div>
            <div className="text-xs text-white/80 truncate" title={user?.email}>{user?.email}</div>
            <div className="text-[10px] text-white/50 mb-1">
              {user?.role === "admin" ? "Administrador" : "Editor"}
            </div>
            <button onClick={() => setShowChangePw(true)}
                    className="w-full sidebar-item !text-white/70 hover:!text-white"
                    data-testid="btn-change-password">
              <SvgIcon d="M15 7a4 4 0 11-8 0 4 4 0 018 0zm-4 7c-3.3 0-6 2.7-6 6h12c0-3.3-2.7-6-6-6z"/>
              <span>Cambiar contraseña</span>
            </button>
            <button onClick={() => setShowPasskeys(true)}
                    className="w-full sidebar-item !text-white/70 hover:!text-white"
                    data-testid="btn-passkeys">
              <SvgIcon d="M12 11c0-3 2-5 5-5s5 2 5 5v1M2 11c0-3 2-5 5-5 1.5 0 2.8.6 3.7 1.6M7 21v-6M14 21v-4c0-3-2-5-5-5s-5 2-5 5v4M17 17v4"/>
              <span>Huella / Face ID</span>
            </button>
            <button onClick={logout} className="w-full sidebar-item !text-red-300 hover:!text-red-200"
                    data-testid="btn-logout">
              <SvgIcon d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 md:ml-0">
        <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 shrink-0"
              aria-label="Abrir menú"
              data-testid="btn-sidebar-open"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold truncate">{currentLabel}</h1>
              <p className="text-xs text-slate-500 truncate">{subtitleMap[view]}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right text-xs text-slate-500 mr-2 hidden sm:block">
              <div>{new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}</div>
              <div className="font-semibold text-slate-700">{clock}</div>
            </div>
            <div className="sm:hidden font-semibold text-slate-700 text-xs">{clock}</div>
          </div>
        </header>
        <div className="flex-1 p-4 md:p-8 overflow-x-hidden">
          {view === "dashboard" && <Dashboard {...commonProps} />}
          {view === "clientes" && <Clientes {...commonProps} />}
          {view === "articulos" && <Articulos {...commonProps} />}
          {view === "servicios" && <Servicios {...commonProps} />}
          {view === "personal" && <Personal {...commonProps} />}
          {view === "proyectos" && <Proyectos {...commonProps} />}
          {view === "ventas" && <Ventas {...commonProps} />}
          {view === "informes" && <Reports />}
          {view === "usuarios" && isAdmin && <Users />}
        </div>
      </main>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
      {showPasskeys && <PasskeysModal onClose={() => setShowPasskeys(false)} />}
    </div>
  );
}

function Root() {
  const { status } = useAuth();
  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0E1726" }}>
        <div className="text-white/60 text-sm">Cargando...</div>
      </div>
    );
  }
  if (status === "guest") return <Login />;
  return <Shell />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="bottom-right" richColors />
      <Root />
    </AuthProvider>
  );
}
